import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import { sendOrderEmails } from '../services/emailService';
import toast from 'react-hot-toast';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { fr } from 'date-fns/locale';

const Checkout = () => {
  const { cartItems, getTotal, clearCart } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  
  // ✅ Récupérer le mode de paiement depuis le panier ou utiliser 'livraison' par défaut
  const initialPaymentMethod = location.state?.paymentMethod || 'livraison';
  
  const [loading, setLoading] = useState(false);
  const [orderPlaced, setOrderPlaced] = useState(false);
  const [orderNumber, setOrderNumber] = useState('');
  const [emailSent, setEmailSent] = useState(false);

  // ✅ AJOUT : Vérifier si l'utilisateur est connecté
  useEffect(() => {
    if (!user) {
      sessionStorage.setItem('redirectAfterLogin', '/checkout');
      toast.error('Veuillez vous connecter pour accéder à la validation de commande');
      navigate('/login');
      return;
    }
    
    if (cartItems.length === 0) {
      toast.error('Votre panier est vide');
      navigate('/products');
      return;
    }
  }, [user, cartItems, navigate]);

  const [formData, setFormData] = useState({
    firstName: user?.firstName || '',
    lastName: user?.lastName || '',
    email: user?.email || '',
    phone: user?.phone || '',
    address: '',
    city: '',
    postalCode: '',
    floor: '',
    hasElevator: false,
    depositAtDoor: false,
    notes: '',
    paymentMethod: initialPaymentMethod // ✅ Utiliser la méthode du panier
  });

  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState(null);

  const packCount = cartItems.reduce((sum, item) => sum + item.quantity, 0);
  const subtotalAmount = getTotal();

  const calculateDeliveryFee = (count) => {
    if (count <= 5) return 9.90;
    if (count <= 10) return 14.90;
    if (count <= 20) return 19.90;
    if (count <= 40) return 29.90;
    return 0;
  };

  const deliveryAmount = calculateDeliveryFee(packCount);
  const totalAmount = subtotalAmount + deliveryAmount;

  const minDate = new Date();
  minDate.setDate(minDate.getDate() + 2);

  const maxDate = new Date();
  maxDate.setDate(maxDate.getDate() + 30);

  const isWeekday = (date) => {
    const day = date.getDay();
    return day !== 0;
  };

  const generateTimeSlots = () => {
    const slots = [];
    for (let hour = 8; hour <= 20; hour++) {
      const timeStr = `${hour.toString().padStart(2, '0')}:00`;
      slots.push({ value: timeStr, label: `${hour}h00` });
    }
    return slots;
  };

  const timeSlots = generateTimeSlots();

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  // ✅ Icônes pour les options de paiement
  const Icons = {
    CreditCard: () => (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
      </svg>
    ),
    Cash: () => (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
      </svg>
    )
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.firstName || !formData.lastName || !formData.email || 
        !formData.address || !formData.city || !formData.postalCode) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }

    if (!selectedDate) {
      toast.error('Veuillez sélectionner une date de livraison');
      return;
    }

    if (!selectedTime) {
      toast.error('Veuillez sélectionner un créneau horaire');
      return;
    }

    if (packCount < 1) {
      toast.error('Minimum 1 article pour valider la commande');
      return;
    }

    setLoading(true);

    try {
      let formattedDate = null;
      if (selectedDate) {
        const year = selectedDate.getFullYear();
        const month = String(selectedDate.getMonth() + 1).padStart(2, '0');
        const day = String(selectedDate.getDate()).padStart(2, '0');
        formattedDate = `${year}-${month}-${day}`;
      }

      const orderData = {
        userId: user?.id || null,
        customerName: `${formData.firstName} ${formData.lastName}`,
        customerEmail: formData.email,
        customerPhone: formData.phone || '',
        items: cartItems.map(item => ({
          productId: item.id,
          name: item.name,
          quantity: item.quantity,
          price: typeof item.price === 'string' ? parseFloat(item.price) : item.price
        })),
        totalAmount: subtotalAmount,
        deliveryFee: deliveryAmount,
        grandTotal: totalAmount,
        deliveryAddress: formData.address,
        deliveryCity: formData.city,
        deliveryPostal: formData.postalCode,
        deliveryFloor: formData.floor ? parseInt(formData.floor) : null,
        hasElevator: formData.hasElevator || false,
        deliveryDate: formattedDate,
        deliverySlot: selectedTime || 'flexible',
        depositAtDoor: formData.depositAtDoor || false,
        notes: formData.notes || '',
        paymentMethod: formData.paymentMethod || 'livraison'
      };

      console.log('📦 orderData COMPLET:', JSON.stringify(orderData, null, 2));

      const response = await api.post('/orders', orderData);
      const order = response.data;
      setOrderNumber(order.order_number);
      
      const emailResult = await sendOrderEmails(orderData, formData.email);
      
      if (emailResult.success) {
        setEmailSent(true);
        toast.success('Email de confirmation envoyé !');
      } else {
        toast.error('Commande validée mais email non envoyé. Vérifiez vos identifiants EmailJS.');
        console.error('Erreur EmailJS:', emailResult);
      }

      setOrderPlaced(true);
      clearCart();

    } catch (error) {
      console.error('Erreur création commande:', error);
      toast.error(error.response?.data?.error || 'Erreur lors de la création de la commande');
    } finally {
      setLoading(false);
    }
  };

  if (cartItems.length === 0 && !orderPlaced) {
    navigate('/cart');
    return null;
  }

  if (orderPlaced) {
    return (
      <div className="container-custom py-20 text-center">
        <div className="max-w-md mx-auto bg-white rounded-2xl shadow-xl p-8">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-10 h-10 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h1 className="text-2xl font-bold mb-2">Commande confirmée !</h1>
          <p className="text-gray-500 mb-4">Merci pour votre commande</p>
          <p className="text-lg font-semibold text-primary mb-4">N° {orderNumber}</p>
          
          {emailSent ? (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
              <p className="text-green-700 text-sm">✅ Un email de confirmation vous a été envoyé</p>
            </div>
          ) : (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
              <p className="text-yellow-700 text-sm">⚠️ Vérifiez vos emails (pensez à vérifier les spams)</p>
            </div>
          )}
          
          <p className="text-gray-600 mb-6">Vous recevrez un email avec le récapitulatif de votre commande.</p>
          <Link to="/" className="btn-primary inline-block">Retour à l'accueil</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-cream min-h-screen py-12">
      <div className="container-custom">
        <h1 className="text-3xl font-bold mb-2">Validation de commande</h1>
        <p className="text-gray-500 mb-8">Complétez vos informations pour finaliser votre commande</p>

        <div className="grid lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="bg-white rounded-xl shadow-md p-6">
                <h2 className="text-xl font-bold mb-4">Informations de livraison</h2>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Prénom *</label>
                    <input type="text" name="firstName" value={formData.firstName} onChange={handleChange} className="w-full px-4 py-2 border rounded-lg" required />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Nom *</label>
                    <input type="text" name="lastName" value={formData.lastName} onChange={handleChange} className="w-full px-4 py-2 border rounded-lg" required />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Email *</label>
                    <input type="email" name="email" value={formData.email} onChange={handleChange} className="w-full px-4 py-2 border rounded-lg" required />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Téléphone</label>
                    <input type="tel" name="phone" value={formData.phone} onChange={handleChange} className="w-full px-4 py-2 border rounded-lg" />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium mb-1">Adresse *</label>
                    <input type="text" name="address" value={formData.address} onChange={handleChange} className="w-full px-4 py-2 border rounded-lg" required />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Code postal *</label>
                    <input type="text" name="postalCode" value={formData.postalCode} onChange={handleChange} className="w-full px-4 py-2 border rounded-lg" required />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Ville *</label>
                    <input type="text" name="city" value={formData.city} onChange={handleChange} className="w-full px-4 py-2 border rounded-lg" required />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Étage</label>
                    <input type="number" name="floor" value={formData.floor} onChange={handleChange} className="w-full px-4 py-2 border rounded-lg" />
                  </div>
                  <div className="flex items-center">
                    <label className="flex items-center gap-2">
                      <input type="checkbox" name="hasElevator" checked={formData.hasElevator} onChange={handleChange} />
                      Ascenseur
                    </label>
                  </div>
                </div>
              </div>

              {/* ✅ AJOUT : Section Mode de paiement (comme dans Cart.js) */}
              <div className="bg-white rounded-xl shadow-md p-6">
                <h2 className="text-xl font-bold mb-4">Mode de paiement</h2>
                <div className="space-y-3">
                  <label className="flex items-center gap-3 p-3 border rounded-lg cursor-pointer hover:bg-gray-50">
                    <input 
                      type="radio" 
                      name="paymentMethod" 
                      value="livraison" 
                      checked={formData.paymentMethod === 'livraison'} 
                      onChange={handleChange} 
                      className="w-4 h-4 text-secondary" 
                    />
                    <Icons.Cash />
                    <div>
                      <p className="font-medium">Payer à la livraison</p>
                      <p className="text-xs text-gray-500">Règlement en espèces ou carte directement au livreur</p>
                    </div>
                  </label>
                  
                  <label className="flex items-center gap-3 p-3 border rounded-lg cursor-pointer hover:bg-gray-50">
                    <input 
                      type="radio" 
                      name="paymentMethod" 
                      value="online" 
                      checked={formData.paymentMethod === 'online'} 
                      onChange={handleChange} 
                      className="w-4 h-4 text-secondary" 
                    />
                    <Icons.CreditCard />
                    <div>
                      <p className="font-medium">Payer en ligne</p>
                      <p className="text-xs text-gray-500">Paiement sécurisé par carte bancaire (un lien de paiement vous sera envoyé par email)</p>
                    </div>
                  </label>
                </div>
              </div>

              <div className="bg-white rounded-xl shadow-md p-6">
                <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  Choisissez votre date et créneau
                </h2>
                
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">Date de livraison *</label>
                    <DatePicker
                      selected={selectedDate}
                      onChange={(date) => setSelectedDate(date)}
                      minDate={minDate}
                      maxDate={maxDate}
                      filterDate={isWeekday}
                      locale={fr}
                      dateFormat="EEEE d MMMM yyyy"
                      className="w-full px-4 py-3 border rounded-lg focus:outline-none focus:border-secondary"
                      placeholderText="Sélectionnez une date"
                      required
                    />
                    <p className="text-xs text-gray-400 mt-1">Livraison possible du mardi au samedi</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2">Créneau horaire *</label>
                    <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2 max-h-48 overflow-y-auto p-2 border rounded-lg">
                      {timeSlots.map((time) => (
                        <button
                          key={time.value}
                          type="button"
                          onClick={() => setSelectedTime(time.value)}
                          className={`p-2 rounded-lg text-center transition border ${
                            selectedTime === time.value
                              ? 'border-secondary bg-secondary/10 text-primary font-semibold'
                              : 'border-gray-200 hover:border-secondary/50'
                          }`}
                        >
                          <span className="text-sm">{time.label}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-xl shadow-md p-6">
                <label className="flex items-start gap-3 cursor-pointer">
                  <input type="checkbox" name="depositAtDoor" checked={formData.depositAtDoor} onChange={handleChange} className="w-5 h-5 mt-0.5" />
                  <div>
                    <h3 className="font-semibold">Dépôt devant la porte</h3>
                    <p className="text-sm text-gray-500">Autorisation de déposer la commande devant ma porte en mon absence.</p>
                  </div>
                </label>
              </div>

              <div className="bg-white rounded-xl shadow-md p-6">
                <h2 className="text-xl font-bold mb-4">Instructions spéciales</h2>
                <textarea name="notes" value={formData.notes} onChange={handleChange} rows="3" placeholder="Code d'accès, interphone..." className="w-full px-4 py-2 border rounded-lg"></textarea>
              </div>

              <button type="submit" disabled={loading} className="w-full btn-primary py-3 disabled:opacity-50">
                {loading ? 'Validation en cours...' : 'Confirmer ma commande'}
              </button>
            </form>
          </div>

          <div className="lg:col-span-1">
            <div className="bg-white rounded-xl shadow-md p-6 sticky top-24">
              <h2 className="text-xl font-bold mb-4">Récapitulatif</h2>
              <div className="space-y-3 max-h-80 overflow-y-auto mb-4">
                {cartItems.map((item) => {
                  const price = typeof item.price === 'string' ? parseFloat(item.price) : item.price;
                  return (
                    <div key={item.id} className="flex gap-3 pb-3 border-b">
                      <div className="w-12 h-12 bg-gray-100 rounded-lg overflow-hidden flex-shrink-0">
                        <img 
                          src={item.image || '/default-product.png'} 
                          alt={item.name} 
                          className="w-full h-full object-cover" 
                        />
                      </div>
                      <div className="flex-1">
                        <p className="text-sm font-medium">{item.name}</p>
                        <p className="text-xs text-gray-500">x{item.quantity}</p>
                      </div>
                      <div className="text-right">
                        <p className="font-bold text-primary">{(price * item.quantity).toFixed(2)} €</p>
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="border-t pt-4 space-y-2">
                <div className="flex justify-between"><span>Sous-total</span><span>{subtotalAmount.toFixed(2)} €</span></div>
                <div className="flex justify-between"><span>Livraison</span><span>{deliveryAmount.toFixed(2)} €</span></div>
                <div className="flex justify-between font-bold text-lg pt-2 border-t">
                  <span>Total</span>
                  <span className="text-primary">{totalAmount.toFixed(2)} €</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Checkout;